import React, { useState } from "react";
import { Button, cn } from "@lemonadetraining/web-ui";
import { isCookieNoticeDismissed, markCookieNoticeDismissed } from "../utils/cookieNoticeDismissal";

type CookieNoticeProps = {
  detailsHref: string;
  opensInNewTab?: boolean;
  /* Wide screens only; the dashboard's sidebar owns the bottom-left corner. */
  side?: "left" | "right";
};

/* Mount after the router: outside it the link must be a plain anchor, and last
 * keeps the skip link the first focusable element. No aria-live: a landmark read
 * once in document order, not announced on every route. */
function CookieNotice({ detailsHref, opensInNewTab = false, side = "left" }: CookieNoticeProps) {
  const [visible, setVisible] = useState(() => !isCookieNoticeDismissed());

  if (!visible) return null;

  const dismiss = () => {
    markCookieNoticeDismissed();
    setVisible(false);
  };

  return (
    <section
      aria-label="Cookie notice"
      data-testid="cookie-notice"
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 flex flex-col gap-3 border-t bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-foreground shadow-lg md:inset-x-auto md:flex-row md:items-center md:justify-between md:bottom-4 md:max-w-xl md:rounded-xl md:border",
        side === "left" ? "md:left-4" : "md:right-4"
      )}
    >
      <p className="text-sm">
        We only use essential cookies and storage: to keep you signed in, remember your settings and
        protect payments. No tracking.
      </p>
      {/* Left on phones: the floating cart owns the bottom-right corner there. */}
      <div className="flex shrink-0 items-center justify-start gap-2 md:justify-end">
        <a
          href={detailsHref}
          className="inline-flex min-h-11 items-center px-2 text-sm font-medium underline underline-offset-[3px] hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
          target={opensInNewTab ? "_blank" : undefined}
          rel={opensInNewTab ? "noopener noreferrer" : undefined}
        >
          Cookie details
          {opensInNewTab && <span className="sr-only"> (opens in a new tab)</span>}
        </a>
        <Button onClick={dismiss} className="min-h-11 min-w-11">
          OK
        </Button>
      </div>
    </section>
  );
}

export default CookieNotice;
